/**
 * The browser's date box takes TYPING as well as the calendar, and nothing
 * said so (punch list #33, Michelle, 19 Oct 2026). Shown under labelled date
 * fields; the compact grid cells carry the same words as a tooltip instead.
 */
export const DATE_HINT = "Type the date (month, day, year) or pick it from the calendar";

export function DateHint() {
  return <span className="date-hint">{DATE_HINT}</span>;
}

/**
 * The attribute that puts a date box under the past-date pop-up
 * (`PastDatePrompt`): every box that sets a due or client date carries it,
 * spread as `{...PAST_DATE_BOX}`. A box for a date that is past by nature
 * (a report's range, time already spent) does not.
 */
export const PAST_DATE_BOX = { "data-past-date": "" } as const;

/** Today in the reader's own timezone, as a date box writes it: `YYYY-MM-DD`. */
export function todayLocal(now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/**
 * Whether to warn: a date, changed from what the box first showed, before
 * today. `YYYY-MM-DD` strings compare as dates.
 */
export function warnsPast(value: string | null, shown: string | null, today: string): boolean {
  return Boolean(value) && value !== shown && value! < today;
}

/**
 * A whole date, not one half-typed. A date box reports each digit of the
 * year as it is typed -- 2027 arrives as 0002, 0020, 0202 and then 2027 --
 * and each of the first three is a "date" long past that nobody meant.
 */
export function isCompleteDate(value: string | null): boolean {
  return value !== null && /^\d{4}-\d{2}-\d{2}$/.test(value) && Number(value.slice(0, 4)) >= 1000;
}

/**
 * Whether a date box's new value opens the pop-up (punch list F170, Jeremy
 * 30 Oct 2026): a whole date, before today, changed from what the box showed
 * when it was first used, and not the one just answered "Use date" for -- so
 * keeping a date never brings the pop-up straight back.
 */
export function promptsPast(
  value: string | null,
  shown: string | null,
  kept: string | null,
  today: string,
): boolean {
  return isCompleteDate(value) && value !== kept && warnsPast(value, shown, today);
}

/** The parts of a date box the pop-up's two buttons touch. */
export interface DateBox {
  value: string;
  focus(): void;
  blur(): void;
  dispatchEvent(event: Event): boolean;
}

/**
 * "Use date": keep the date and leave the box. Leaving it is what saves it
 * where a box saves on leaving (Plan all stages); everywhere else it saves as
 * it always has.
 */
export function keepDate(box: DateBox): void {
  box.blur();
}

/**
 * "Edit date": today in the box, and the cursor in it. Set through the
 * input's own value setter and announced with an `input` event, so React's
 * `onChange` sees it exactly as if it had been typed.
 */
export function editToToday(box: DateBox, today: string): void {
  const setter =
    typeof HTMLInputElement !== "undefined" && box instanceof HTMLInputElement
      ? Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, "value")?.set
      : undefined;
  if (setter) setter.call(box, today);
  else box.value = today;
  box.dispatchEvent(new Event("input", { bubbles: true }));
  box.focus();
}
